import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/useAuth';
import { stopImpersonation } from '../lib/personas';
import './ImpersonationBanner.css';

/**
 * Persistent banner shown whenever an admin is acting as a test persona.
 *
 * It renders from `user.impersonation`, which comes from GET /api/auth/me — the SERVER's
 * answer, never a local flag. That matters more than it sounds: a client-side flag could
 * drift out of step with the cookie and either hide a live impersonation or claim one
 * that has already lapsed. If the server says you are the admin, no banner appears, and
 * that is always the truth.
 *
 * Mounted in every layout rather than on individual pages, because "unmissable" means it
 * cannot be absent from a screen someone happens to navigate to. It is fixed to the
 * viewport and offset by a body rule, so it never scrolls away or covers content.
 */
export function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [returning, setReturning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const impersonation = user?.impersonation;
  if (!impersonation) return null;

  async function handleReturn() {
    setReturning(true);
    setError(null);
    try {
      await stopImpersonation();
      // Re-ask the engine who we are before navigating, so the banner disappears because
      // the server agrees it should — not because we assumed the call worked.
      await refresh();
      navigate('/admin');
    } catch {
      setError('Could not return. Reload the page.');
      setReturning(false);
    }
  }

  return (
    <div className="impersonation" role="status" aria-live="polite">
      <div className="impersonation__inner">
        <span className="impersonation__flag">Viewing as</span>

        <span className="impersonation__persona">
          {impersonation.personaLabel}
          <span className="impersonation__email mono">{user?.email}</span>
        </span>

        <span className="impersonation__admin">
          You are signed in as {impersonation.adminEmail}
        </span>

        {error && <span className="impersonation__error">{error}</span>}

        <button
          type="button"
          className="impersonation__return"
          onClick={() => void handleReturn()}
          disabled={returning}
        >
          {returning ? 'Returning…' : 'Return to admin'}
        </button>
      </div>
    </div>
  );
}
